import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { AuthService } from '../../service/auth.service';
import { NotificationHandler } from '../../helper/notification.handler';
import { UrlHelper } from '../../helper/url.helper';
import { UserService } from '../../service/user.service';
import { TenantService } from '../../service/tenant.service';
import { UserStatusType } from '../../model/auth/User';
import { LocalStorageService } from 'ngx-webstorage';
import * as Sentry from '@sentry/angular';
import { environment } from '../../../environments/environment';
import smartlookClient from 'smartlook-client';

@Component({
  selector: 'pass-reset',
  templateUrl: './pass-reset.component.html',
  styleUrls: ['./pass-reset.component.scss']
})
export class PassResetComponent implements OnInit {

  emailField = new FormControl('', [
    Validators.email, Validators.required, Validators.minLength(5), Validators.maxLength(128)
  ]);

  resetForm: FormGroup = new FormGroup({
    email: this.emailField,
  });

  inProgress = false;

  constructor(private router: Router,
              private route: ActivatedRoute,
              private authService: AuthService,
              private userService: UserService,
              private tenantService: TenantService,
              private localStorageService: LocalStorageService) {
  }

  async ngOnInit() {
    const email = decodeURIComponent(this.route.snapshot.queryParamMap.get('email') || '');
    const code = this.route.snapshot.queryParamMap.get('code');

    const session = await this.authService.getSession();
    if (session && session.isValid()) {
      if (email && code) {
        await this.signOut();
        this.tenantService.loadTenantDetails();
      } else {
        NotificationHandler.handleWarning($localize`:General|This page is not available for authorized users@@general-this-page-is-not-available-for-authorized-users-message:This page is not available for authorized users message`);
        await this.router.navigate([UrlHelper.DEFAULT_AUTH_PATH], {replaceUrl: true});
      }
    }

    if (email) this.emailField.setValue(email);
  }

  async signOut() {
    try {
      await this.authService.logout();
      this.localStorageService.clear();
      Sentry.configureScope(scope => scope.setUser(null));
      if (environment.smartlook.enabled) smartlookClient.anonymize();
    } catch (e) {
      NotificationHandler.handleError(e);
    }
  }

  shouldDisableSubmit() {
    return !this.resetForm.valid;
  }

  get signInUrl() {
    return UrlHelper.SIGN_IN_PATH;
  }

  resetPass() {
    this.inProgress = true;

    const email = this.emailField.value as string;

    // eslint-disable-next-line @typescript-eslint/no-misused-promises
    this.userService.userExists(email).subscribe(user => {
      if (!user || !user.id) {
        NotificationHandler.handleWarning($localize`:Password Reset|Cannot reset password for not existent user message@@cannot-reset-password-for-not-existent-user-error:User profile with this address was not found and therefore password cannot be reset`);

        this.inProgress = false;

        return;
      }

      this.userService.migrateUser(true, user.id).subscribe(result => {
        if (result.retry_auth) {
          void this.attemptPasswordReset(user.id || '', email, user.email_verified || false, result.status);
        } else {
          this.showFailedNotification();
        }
      }, error => {
        this.showFailedNotification(error as Error);
      });
    }, error => {
      this.showFailedNotification(error as Error);
    });
  }

  private showFailedNotification(error?: Error) {
    NotificationHandler.handleError($localize`:Password Reset|Password reset request failed message@@password-reset-request-failed-error:Password reset request failed`, error);
    this.inProgress = false;
  }

  private async attemptPasswordReset(username: string, email: string, emailVerified: boolean, status: UserStatusType) {
    try {
      if (emailVerified && status != 'FORCE_CHANGE_PASSWORD') {
        await this.authService.resetPassword(username);

        await this.navigateToConfirm(username);
      } else {
        this.resetPassForUnconfirmedUser(email, username, status == 'FORCE_CHANGE_PASSWORD');
      }
    } catch (err) {
      console.error(err);

      const code: string = (err as { [key: string]: string | undefined })['code'] as string;

      if (code === 'InvalidParameterException') {
        this.resetPassForUnconfirmedUser(email, username, status == 'FORCE_CHANGE_PASSWORD');
      } else if (code === 'UserNotFoundException') {
        NotificationHandler.handleWarning($localize`:Password Reset|Cannot reset password for not existent user message@@cannot-reset-password-for-not-existent-user-error:User profile with this address was not found and therefore password cannot be reset`);
        this.inProgress = false;
      } else {
        this.showFailedNotification(err as Error);
      }
    }
  }

  private resetPassForUnconfirmedUser(email: string, username: string, stringCode: boolean) {
    // NotificationHandler.handleError($localize`:Password Reset|Password reset invalid parameter message@@password-reset-invalid-parameter-error:Cannot reset a password for the user as his email address is not verified`, err);

    const tenant = this.tenantService.getTenant();
    // eslint-disable-next-line @typescript-eslint/no-misused-promises
    this.userService.forceResetUserPassword(email, tenant?.name, tenant?.domain).subscribe(async () => {
      await this.navigateToConfirm(username, stringCode);
      this.inProgress = false;
    }, err => {
      this.showFailedNotification(err as Error);
    });
  }

  private async navigateToConfirm(username: string, forcedCode = false) {
    const email = encodeURIComponent(this.emailField.value as string);

    NotificationHandler.notification($localize`:Password Reset|Password reset code info message@@reset-password-code-info-message:An email message with a temporary confirmation code was sent to your inbox. Please use it to set a new password.`);

    await this.router.navigate([`${UrlHelper.PASS_RESET_CONFIRM_PATH}${email}/${username}`], {
      queryParams: {
        forced_code: forcedCode
      }
    });
  }
}
